import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { CheckCircleIcon, LockIcon, BookIcon, AwardIcon, UsersIcon, PlayIcon, StarIcon } from 'lucide-react';
type CareerJourneyMapProps = {
  onContinue: () => void;
};
export const CareerJourneyMap: React.FC<CareerJourneyMapProps> = ({
  onContinue
}) => {
  const [unlockedStages, setUnlockedStages] = useState(1);
  const [xp, setXp] = useState(450);
  const journeyStages = [{
    id: 'skill-gaps',
    title: 'Skill Gaps',
    description: 'Identify and address your key skill development areas',
    icon: <BookIcon size={24} data-id="element-923" />,
    xpRequired: 0,
    tasks: [{
      name: 'Complete skill assessment',
      completed: true,
      xp: 50
    }, {
      name: 'Create development plan',
      completed: false,
      xp: 75
    }, {
      name: 'Complete first skill module',
      completed: false,
      xp: 100
    }]
  }, {
    id: 'learning',
    title: 'Learning Modules',
    description: 'Access tailored courses to build your career skills',
    icon: <BookIcon size={24} data-id="element-924" />,
    xpRequired: 500,
    tasks: [{
      name: 'Complete UX fundamentals',
      completed: false,
      xp: 150
    }, {
      name: 'Design your first prototype',
      completed: false,
      xp: 200
    }, {
      name: 'Submit project for review',
      completed: false,
      xp: 150
    }]
  }, {
    id: 'mentor',
    title: 'Mentor Match',
    description: 'Connect with industry professionals who can guide you',
    icon: <UsersIcon size={24} data-id="element-925" />,
    xpRequired: 1000,
    tasks: [{
      name: 'Complete mentor preferences',
      completed: false,
      xp: 50
    }, {
      name: 'Schedule first mentor call',
      completed: false,
      xp: 100
    }, {
      name: 'Create goals with mentor',
      completed: false,
      xp: 150
    }]
  }, {
    id: 'simulation',
    title: 'Career Simulation',
    description: 'Experience a day in the life of your matched career',
    icon: <PlayIcon size={24} data-id="element-926" />,
    xpRequired: 1500,
    tasks: [{
      name: 'Complete pre-simulation quiz',
      completed: false,
      xp: 100
    }, {
      name: 'Participate in simulation',
      completed: false,
      xp: 300
    }, {
      name: 'Debrief and reflection',
      completed: false,
      xp: 150
    }]
  }];
  const completeTask = (stageIndex: number, taskIndex: number) => {
    const task = journeyStages[stageIndex].tasks[taskIndex];
    if (!task.completed) {
      const newXp = xp + task.xp;
      setXp(newXp);
      // Check if completing this task unlocks a new stage
      const nextStage = journeyStages[unlockedStages];
      if (nextStage && newXp >= nextStage.xpRequired) {
        setUnlockedStages(unlockedStages + 1);
      }
    }
  };
  const isStageUnlocked = (index: number) => {
    return index < unlockedStages;
  };
  const isStageActive = (index: number) => {
    return index === unlockedStages - 1;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-927">
      <div className="mb-6 flex justify-between items-center" data-id="element-928">
        <div data-id="element-929">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-930">
            Your Career Journey Map
          </h1>
          <p className="text-[#B8C0E0]" data-id="element-931">
            Complete each milestone to progress on your path
          </p>
        </div>
        <div className="bg-[#1C2541] rounded-lg p-2 flex items-center" data-id="element-932">
          <StarIcon size={20} className="text-[#FFD700] mr-2" data-id="element-933" />
          <span className="font-bold" data-id="element-934">{xp} XP</span>
        </div>
      </div>
      <div className="relative" data-id="element-935">
        {/* Connection lines between journey stages */}
        <div className="absolute left-6 top-12 bottom-12 w-1 bg-[#3A506B] z-0" data-id="element-936"></div>
        <div className="space-y-6 relative z-10" data-id="element-937">
          {journeyStages.map((stage, index) => {
          const isUnlocked = isStageUnlocked(index);
          const isActive = isStageActive(index);
          const isLocked = !isUnlocked;
          return <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.2
          }} data-id="element-938">
                <Card className={`relative ${isActive ? 'border-[#7D56F4]' : ''} ${isLocked ? 'opacity-60' : ''}`} data-id="element-939">
                  {isLocked && <div className="absolute inset-0 bg-[#0A1128]/50 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center z-20" data-id="element-940">
                      <LockIcon size={30} className="text-[#3A506B] mb-2" data-id="element-941" />
                      <p className="text-center text-[#B8C0E0]" data-id="element-942">
                        Requires {stage.xpRequired} XP to unlock
                      </p>
                      <p className="text-center text-[#B8C0E0] text-sm mt-1" data-id="element-943">
                        You need {stage.xpRequired - xp} more XP
                      </p>
                    </div>}
                  <div className="flex" data-id="element-944">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${isActive ? 'bg-gradient-to-r from-[#7D56F4] to-[#19D3DA]' : isUnlocked ? 'bg-[#19D3DA]' : 'bg-[#3A506B]'}`} data-id="element-945">
                      {stage.icon}
                    </div>
                    <div className="flex-1" data-id="element-946">
                      <div className="flex justify-between items-center mb-2" data-id="element-947">
                        <h3 className="text-lg font-semibold" data-id="element-948">{stage.title}</h3>
                        {stage.xpRequired > 0 && <div className="text-sm text-[#B8C0E0]" data-id="element-949">
                            Required: {stage.xpRequired} XP
                          </div>}
                      </div>
                      <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-950">
                        {stage.description}
                      </p>
                      <div className="space-y-2" data-id="element-951">
                        {stage.tasks.map((task, taskIndex) => <div key={taskIndex} className="flex items-center justify-between" data-id="element-952">
                            <div className="flex items-center" data-id="element-953">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-2 ${task.completed ? 'bg-[#19D3DA]/20 text-[#19D3DA]' : 'bg-[#1C2541] text-[#B8C0E0]'}`} data-id="element-954">
                                {task.completed ? <CheckCircleIcon size={14} data-id="element-955" /> : <span className="text-xs" data-id="element-956">
                                    {taskIndex + 1}
                                  </span>}
                              </div>
                              <span className={`text-sm ${task.completed ? 'line-through text-[#B8C0E0]' : 'text-white'}`} data-id="element-957">
                                {task.name}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-958">
                              <span className="text-xs text-[#B8C0E0] mr-2" data-id="element-959">
                                +{task.xp} XP
                              </span>
                              {isUnlocked && !task.completed && <button onClick={() => completeTask(index, taskIndex)} className="text-xs bg-[#1C2541] hover:bg-[#3A506B] py-1 px-2 rounded" data-id="element-960">
                                  Complete
                                </button>}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1,
      duration: 0.5
    }} className="mt-8 flex justify-center" data-id="element-961">
        <button onClick={onContinue} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full" data-id="element-962">
          Go to Gamification Hub
        </button>
      </motion.div>
    </motion.div>;
};